import React, { useState } from 'react';
import { Search, CreditCard as Edit2, Save, X, Plus, Trash2, Upload, FileText, CircleAlert as AlertCircle, CircleCheck as CheckCircle, History } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import InputMask from 'react-input-mask-next';

interface Contract {
  id: string;
  client_name: string;
  cpf: string;
  phone: string;
  relative_phones: { numero: string; relacao: string }[];
  email: string;
  address: string;
  business_address: string;
  pix_key: string;
  bank: string;
  loan_amount: number;
  installments: number;
  total_amount: number;
  interest_rate: number;
  outstanding_balance: number;
  status: string;
  approval_status: string;
  payment_status: string;
  ruta: number;
  seller_id: string;
  unit_id: string;
  created_at: string;
  approved_at: string | null;
  observations?: string;
  entry_time?: string;
  exit_time?: string;
  seller?: {
    email: string;
  };
  unit?: {
    name: string;
  };
  contract_files: {
    id: string;
    file_name: string;
    file_url: string;
    file_type: string;
  }[];
  installments_data: {
    id: string;
    due_date: string;
    amount: number;
    status: string;
    paid_amount?: number;
    interest?: number;
  }[];
}

interface EditableContract extends Contract {
  isEditing: {
    personal: boolean;
    address: boolean;
    banking: boolean;
    contract: boolean;
    operational: boolean;
    status: boolean;
  };
}

interface RelativePhone {
  numero: string;
  relacao: string;
}

export default function ClientEdit() {
  const { user } = useAuth();
  const [searchType, setSearchType] = useState<'name' | 'cpf' | 'contract' | 'phone'>('name');
  const [searchValue, setSearchValue] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Contract[]>([]);
  const [selectedContract, setSelectedContract] = useState<EditableContract | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editedData, setEditedData] = useState<Partial<Contract>>({});
  const [editedInstallments, setEditedInstallments] = useState<any[]>([]);
  const [editedRelativePhones, setEditedRelativePhones] = useState<RelativePhone[]>([]);

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      setMessage({ type: 'error', text: 'Por favor, preencha o campo de busca' });
      return;
    }

    setSearching(true);
    setMessage(null);
    setSearchResults([]);

    try {
      let query = supabase
        .from('contracts')
        .select(`
          *,
          seller:seller_id(email),
          unit:unit_id(name),
          contract_files(*),
          installments_data:installments(*)
        `)
        .eq('unit_id', user?.unit_id);

      if (searchType === 'name') {
        query = query.ilike('client_name', `%${searchValue}%`);
      } else if (searchType === 'cpf') {
        const cleanCPF = searchValue.replace(/\D/g, '');
        query = query.eq('cpf', cleanCPF);
      } else if (searchType === 'contract') {
        query = query.eq('id', searchValue);
      } else if (searchType === 'phone') {
        const cleanPhone = searchValue.replace(/\D/g, '');
        query = query.ilike('phone', `%${cleanPhone}%`);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      if (!data || data.length === 0) {
        setMessage({ type: 'error', text: 'Nenhum cliente encontrado' });
      } else {
        setSearchResults(data);
      }
    } catch (err) {
      console.error('Error searching:', err);
      setMessage({ type: 'error', text: 'Erro ao buscar cliente' });
    } finally {
      setSearching(false);
    }
  };

  const handleSelectContract = (contract: Contract) => {
    const editableContract: EditableContract = {
      ...contract,
      isEditing: {
        personal: false,
        address: false,
        banking: false,
        contract: false,
        operational: false,
        status: false,
      },
    };
    setSelectedContract(editableContract);
    setEditedData(contract);
    setEditedInstallments(contract.installments_data || []);
    setEditedRelativePhones(contract.relative_phones || []);
    setSearchResults([]);
    setSearchValue('');
  };

  const toggleEdit = (section: keyof EditableContract['isEditing']) => {
    if (!selectedContract) return;

    setSelectedContract({
      ...selectedContract,
      isEditing: {
        ...selectedContract.isEditing,
        [section]: !selectedContract.isEditing[section],
      },
    });
  };

  const handleSave = async () => {
    if (!selectedContract) return;

    setSaving(true);
    setMessage(null);

    try {
      const { error: contractError } = await supabase
        .from('contracts')
        .update({
          client_name: editedData.client_name,
          cpf: editedData.cpf,
          phone: editedData.phone,
          email: editedData.email,
          address: editedData.address,
          business_address: editedData.business_address,
          pix_key: editedData.pix_key,
          bank: editedData.bank,
          loan_amount: editedData.loan_amount,
          installments: editedData.installments,
          total_amount: editedData.total_amount,
          interest_rate: editedData.interest_rate,
          ruta: editedData.ruta,
          observations: editedData.observations,
          entry_time: editedData.entry_time,
          exit_time: editedData.exit_time,
          relative_phones: editedRelativePhones,
        })
        .eq('id', selectedContract.id);

      if (contractError) throw contractError;

      for (const installment of editedInstallments) {
        if (installment.id) {
          const { error: instError } = await supabase
            .from('installments')
            .update({
              due_date: installment.due_date,
              amount: installment.amount,
              status: installment.status,
              paid_amount: installment.paid_amount,
              interest: installment.interest,
            })
            .eq('id', installment.id);

          if (instError) throw instError;
        }
      }

      setMessage({ type: 'success', text: 'Alterações salvas com sucesso!' });

      setSelectedContract({
        ...selectedContract,
        ...editedData,
        installments_data: editedInstallments,
        relative_phones: editedRelativePhones,
        isEditing: {
          personal: false,
          address: false,
          banking: false,
          contract: false,
          operational: false,
          status: false,
        },
      });

      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Error saving:', err);
      setMessage({ type: 'error', text: 'Erro ao salvar alterações' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedContract) return;

    const confirmDelete = window.confirm(
      `Tem certeza que deseja deletar o contrato de ${selectedContract.client_name}?\n\n` +
      `Esta ação irá remover:\n` +
      `- O contrato\n` +
      `- Todas as parcelas\n` +
      `- Todos os arquivos anexados\n` +
      `- Todas as transações relacionadas\n\n` +
      `O CPF do cliente será adicionado à BLACKLIST.\n\n` +
      `ESTA AÇÃO NÃO PODE SER DESFEITA!`
    );

    if (!confirmDelete) return;

    const blacklistReason = window.prompt(
      'Informe o MOTIVO da exclusão (obrigatório para blacklist):\nEx: Calote, Fraude, Cliente problemático'
    );

    if (!blacklistReason || !blacklistReason.trim()) {
      alert('É obrigatório informar o motivo da exclusão.');
      return;
    }

    setDeleting(true);
    setMessage(null);

    try {
      // 1. Deletar late_fee_allocations relacionadas
      const { error: lateFeeError } = await supabase
        .from('late_fee_allocations')
        .delete()
        .or(`source_contract_id.eq.${selectedContract.id},target_contract_id.eq.${selectedContract.id}`);

      if (lateFeeError) throw lateFeeError;

      // 2. Deletar circuit_route_stops relacionadas
      const { error: circuitStopsError } = await supabase
        .from('circuit_route_stops')
        .delete()
        .eq('contract_id', selectedContract.id);

      if (circuitStopsError) throw circuitStopsError;

      // 3. Deletar client_edit_requests
      const { error: editRequestsError } = await supabase
        .from('client_edit_requests')
        .delete()
        .eq('contract_id', selectedContract.id);

      if (editRequestsError) throw editRequestsError;

      // 4. Deletar client_self_registrations
      const { error: selfRegError } = await supabase
        .from('client_self_registrations')
        .delete()
        .eq('contract_id', selectedContract.id);

      if (selfRegError) throw selfRegError;

      // 5. Deletar contract_modification_requests
      const { error: modRequestsError } = await supabase
        .from('contract_modification_requests')
        .delete()
        .eq('contract_id', selectedContract.id);

      if (modRequestsError) throw modRequestsError;

      // 6. Deletar installments
      const { error: installmentsError } = await supabase
        .from('installments')
        .delete()
        .eq('contract_id', selectedContract.id);

      if (installmentsError) throw installmentsError;

      // 7. Deletar contract_files
      const { error: filesError } = await supabase
        .from('contract_files')
        .delete()
        .eq('contract_id', selectedContract.id);

      if (filesError) throw filesError;

      // 8. Finalmente, deletar o contrato
      const { error: contractError } = await supabase
        .from('contracts')
        .delete()
        .eq('id', selectedContract.id);

      if (contractError) throw contractError;

      // 9. Adicionar CPF à blacklist
      const cpfDigits = selectedContract.cpf.replace(/\D/g, '');
      await supabase
        .from('cpf_blacklist')
        .upsert({
          cpf: cpfDigits,
          client_name: selectedContract.client_name,
          reason: blacklistReason.trim(),
          deleted_from_unit_id: user?.unit_id || null,
          deleted_by_user_id: user?.id || null,
        }, { onConflict: 'cpf' });

      setMessage({ type: 'success', text: 'Contrato deletado e CPF adicionado à blacklist!' });

      setTimeout(() => {
        setSelectedContract(null);
        setMessage(null);
      }, 2000);
    } catch (err) {
      console.error('Error deleting contract:', err);
      const detail = err && typeof err === 'object' && 'message' in err ? `: ${(err as any).message}` : '';
      setMessage({ type: 'error', text: `Erro ao deletar contrato${detail}` });
    } finally {
      setDeleting(false);
    }
  };

  const handleAddRelativePhone = () => {
    setEditedRelativePhones([...editedRelativePhones, { numero: '', relacao: '' }]);
  };

  const handleRemoveRelativePhone = (index: number) => {
    setEditedRelativePhones(editedRelativePhones.filter((_, i) => i !== index));
  };

  const handleRelativePhoneChange = (index: number, field: 'numero' | 'relacao', value: string) => {
    const updated = [...editedRelativePhones];
    updated[index][field] = value;
    setEditedRelativePhones(updated);
  };

  const handleInstallmentChange = (index: number, field: string, value: any) => {
    const updated = [...editedInstallments];
    updated[index][field] = value;
    setEditedInstallments(updated);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  if (user?.role !== 'manager') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Acesso Negado</h2>
          <p className="text-gray-600">Apenas gerentes podem acessar esta página.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-800">Editar Cliente</h1>
      </div>

      {message && (
        <div
          className={`p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {!selectedContract && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Buscar Cliente</h2>

          <div className="space-y-4">
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  value="name"
                  checked={searchType === 'name'}
                  onChange={(e) => setSearchType(e.target.value as any)}
                  className="w-4 h-4"
                />
                <span>Nome</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  value="cpf"
                  checked={searchType === 'cpf'}
                  onChange={(e) => setSearchType(e.target.value as any)}
                  className="w-4 h-4"
                />
                <span>CPF</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  value="phone"
                  checked={searchType === 'phone'}
                  onChange={(e) => setSearchType(e.target.value as any)}
                  className="w-4 h-4"
                />
                <span>Telefone</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  value="contract"
                  checked={searchType === 'contract'}
                  onChange={(e) => setSearchType(e.target.value as any)}
                  className="w-4 h-4"
                />
                <span>Código do Contrato</span>
              </label>
            </div>

            <div className="flex gap-2">
              {searchType === 'cpf' ? (
                <InputMask
                  mask="999.999.999-99"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Digite o CPF"
                  className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder={
                    searchType === 'name'
                      ? 'Digite o nome do cliente'
                      : searchType === 'phone'
                      ? 'Digite o telefone'
                      : 'Digite o código do contrato'
                  }
                  className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              )}
              <button
                onClick={handleSearch}
                disabled={searching}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                {searching ? 'Buscando...' : 'Buscar'}
              </button>
            </div>
          </div>

          {searchResults.length > 0 && (
            <div className="mt-6 space-y-3">
              <h3 className="font-semibold text-gray-800">Resultados da Busca:</h3>
              {searchResults.map((contract) => (
                <div
                  key={contract.id}
                  onClick={() => handleSelectContract(contract)}
                  className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-gray-800">{contract.client_name}</h4>
                      <p className="text-sm text-gray-600">CPF: {contract.cpf}</p>
                      <p className="text-sm text-gray-600">
                        Contrato: {formatCurrency(contract.loan_amount)} em {contract.installments}x
                      </p>
                      <p className="text-sm text-gray-600">
                        Status: <span className="font-medium">{contract.status}</span>
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        contract.approval_status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : contract.approval_status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {contract.approval_status === 'approved'
                        ? 'Aprovado'
                        : contract.approval_status === 'pending'
                        ? 'Pendente'
                        : 'Rejeitado'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedContract && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                setSelectedContract(null);
                setEditedData({});
                setEditedInstallments([]);
                setEditedRelativePhones([]);
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 flex items-center gap-2"
            >
              <X className="w-5 h-5" />
              Voltar à busca
            </button>
            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                disabled={deleting || saving}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                <Trash2 className="w-5 h-5" />
                {deleting ? 'Deletando...' : 'Deletar Contrato'}
              </button>
              <button
                onClick={handleSave}
                disabled={saving || deleting}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Dados Pessoais</h2>
              <button
                onClick={() => toggleEdit('personal')}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
              >
                <Edit2 className="w-4 h-4" />
                {selectedContract.isEditing.personal ? 'Cancelar' : 'Editar'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                {selectedContract.isEditing.personal ? (
                  <input
                    type="text"
                    value={editedData.client_name || ''}
                    onChange={(e) => setEditedData({ ...editedData, client_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-800">{selectedContract.client_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CPF</label>
                {selectedContract.isEditing.personal ? (
                  <InputMask
                    mask="999.999.999-99"
                    value={editedData.cpf || ''}
                    onChange={(e) => setEditedData({ ...editedData, cpf: e.target.value.replace(/\D/g, '') })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-800">{selectedContract.cpf}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                {selectedContract.isEditing.personal ? (
                  <InputMask
                    mask="(99) 99999-9999"
                    value={editedData.phone || ''}
                    onChange={(e) => setEditedData({ ...editedData, phone: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-800">{selectedContract.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                {selectedContract.isEditing.personal ? (
                  <input
                    type="email"
                    value={editedData.email || ''}
                    onChange={(e) => setEditedData({ ...editedData, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-800">{selectedContract.email}</p>
                )}
              </div>
            </div>

            {selectedContract.isEditing.personal && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-800">Telefones de Parentes</h3>
                  <button
                    onClick={handleAddRelativePhone}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                  >
                    <Plus className="w-4 h-4" />
                    Adicionar
                  </button>
                </div>
                {editedRelativePhones.map((phone, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <InputMask
                      mask="(99) 99999-9999"
                      value={phone.numero}
                      onChange={(e) => handleRelativePhoneChange(index, 'numero', e.target.value)}
                      placeholder="Telefone"
                      className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      value={phone.relacao}
                      onChange={(e) => handleRelativePhoneChange(index, 'relacao', e.target.value)}
                      placeholder="Relação (ex: Mãe, Pai)"
                      className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => handleRemoveRelativePhone(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Endereços</h2>
              <button
                onClick={() => toggleEdit('address')}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
              >
                <Edit2 className="w-4 h-4" />
                {selectedContract.isEditing.address ? 'Cancelar' : 'Editar'}
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Endereço Residencial
                </label>
                {selectedContract.isEditing.address ? (
                  <textarea
                    value={editedData.address || ''}
                    onChange={(e) => setEditedData({ ...editedData, address: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-800">{selectedContract.address}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Endereço Comercial
                </label>
                {selectedContract.isEditing.address ? (
                  <textarea
                    value={editedData.business_address || ''}
                    onChange={(e) => setEditedData({ ...editedData, business_address: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-800">{selectedContract.business_address}</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Dados Bancários</h2>
              <button
                onClick={() => toggleEdit('banking')}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
              >
                <Edit2 className="w-4 h-4" />
                {selectedContract.isEditing.banking ? 'Cancelar' : 'Editar'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Chave PIX</label>
                {selectedContract.isEditing.banking ? (
                  <input
                    type="text"
                    value={editedData.pix_key || ''}
                    onChange={(e) => setEditedData({ ...editedData, pix_key: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-800">{selectedContract.pix_key}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Banco</label>
                {selectedContract.isEditing.banking ? (
                  <input
                    type="text"
                    value={editedData.bank || ''}
                    onChange={(e) => setEditedData({ ...editedData, bank: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-800">{selectedContract.bank}</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Dados do Contrato</h2>
              <button
                onClick={() => toggleEdit('contract')}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
              >
                <Edit2 className="w-4 h-4" />
                {selectedContract.isEditing.contract ? 'Cancelar' : 'Editar'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor do Empréstimo
                </label>
                {selectedContract.isEditing.contract ? (
                  <input
                    type="number"
                    step="0.01"
                    value={editedData.loan_amount || 0}
                    onChange={(e) =>
                      setEditedData({ ...editedData, loan_amount: parseFloat(e.target.value) })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-800">{formatCurrency(selectedContract.loan_amount)}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total a Pagar
                </label>
                {selectedContract.isEditing.contract ? (
                  <input
                    type="number"
                    step="0.01"
                    value={editedData.total_amount || 0}
                    onChange={(e) =>
                      setEditedData({ ...editedData, total_amount: parseFloat(e.target.value) })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-800">{formatCurrency(selectedContract.total_amount)}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de Parcelas
                </label>
                {selectedContract.isEditing.contract ? (
                  <input
                    type="number"
                    value={editedData.installments || 0}
                    onChange={(e) =>
                      setEditedData({ ...editedData, installments: parseInt(e.target.value) })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-800">{selectedContract.installments}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Taxa de Juros</label>
                {selectedContract.isEditing.contract ? (
                  <input
                    type="number"
                    step="0.01"
                    value={editedData.interest_rate || 0}
                    onChange={(e) =>
                      setEditedData({ ...editedData, interest_rate: parseFloat(e.target.value) })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-800">{selectedContract.interest_rate}%</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Saldo Devedor
                </label>
                <p className="text-gray-800">{formatCurrency(selectedContract.outstanding_balance)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Criação
                </label>
                <p className="text-gray-800">{formatDate(selectedContract.created_at)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Dados Operacionais</h2>
              <button
                onClick={() => toggleEdit('operational')}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
              >
                <Edit2 className="w-4 h-4" />
                {selectedContract.isEditing.operational ? 'Cancelar' : 'Editar'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ruta</label>
                {selectedContract.isEditing.operational ? (
                  <input
                    type="number"
                    value={editedData.ruta || 0}
                    onChange={(e) => setEditedData({ ...editedData, ruta: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-800">{selectedContract.ruta}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Horário de Entrada
                </label>
                {selectedContract.isEditing.operational ? (
                  <input
                    type="time"
                    value={editedData.entry_time || ''}
                    onChange={(e) => setEditedData({ ...editedData, entry_time: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-800">{selectedContract.entry_time || 'N/A'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Horário de Saída
                </label>
                {selectedContract.isEditing.operational ? (
                  <input
                    type="time"
                    value={editedData.exit_time || ''}
                    onChange={(e) => setEditedData({ ...editedData, exit_time: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-gray-800">{selectedContract.exit_time || 'N/A'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendedor</label>
                <p className="text-gray-800">
                  {selectedContract.seller?.email || 'N/A'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unidade</label>
                <p className="text-gray-800">{selectedContract.unit?.name || 'N/A'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Código do Contrato</label>
                <p className="text-gray-800 font-mono text-sm">{selectedContract.id}</p>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
              {selectedContract.isEditing.operational ? (
                <textarea
                  value={editedData.observations || ''}
                  onChange={(e) => setEditedData({ ...editedData, observations: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Adicione observações sobre o contrato..."
                />
              ) : (
                <p className="text-gray-800">{selectedContract.observations || 'Nenhuma observação'}</p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Parcelas</h2>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Parcela</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Vencimento</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Valor</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Status</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Valor Pago</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Juros</th>
                  </tr>
                </thead>
                <tbody>
                  {editedInstallments.map((installment, index) => (
                    <tr key={installment.id} className="border-t">
                      <td className="px-4 py-2 text-sm">{index + 1}</td>
                      <td className="px-4 py-2 text-sm">
                        <input
                          type="date"
                          value={installment.due_date}
                          onChange={(e) =>
                            handleInstallmentChange(index, 'due_date', e.target.value)
                          }
                          className="px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <input
                          type="number"
                          step="0.01"
                          value={installment.amount}
                          onChange={(e) =>
                            handleInstallmentChange(index, 'amount', parseFloat(e.target.value))
                          }
                          className="w-24 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <select
                          value={installment.status}
                          onChange={(e) => handleInstallmentChange(index, 'status', e.target.value)}
                          className="px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="pending">Pendente</option>
                          <option value="paid">Paga</option>
                          <option value="overdue">Atrasada</option>
                        </select>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <input
                          type="number"
                          step="0.01"
                          value={installment.paid_amount || 0}
                          onChange={(e) =>
                            handleInstallmentChange(index, 'paid_amount', parseFloat(e.target.value))
                          }
                          className="w-24 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <input
                          type="number"
                          step="0.01"
                          value={installment.interest || 0}
                          onChange={(e) =>
                            handleInstallmentChange(index, 'interest', parseFloat(e.target.value))
                          }
                          className="w-24 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Arquivos do Contrato</h2>

            {selectedContract.contract_files.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedContract.contract_files.map((file) => (
                  <div
                    key={file.id}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <FileText className="w-8 h-8 text-blue-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 truncate">{file.file_name}</p>
                        <p className="text-sm text-gray-600">{file.file_type}</p>
                        <a
                          href={file.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline mt-2 inline-block"
                        >
                          Visualizar arquivo
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600">Nenhum arquivo anexado</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
